/**
 * A double-ended queue (deque) implementation.
 */
export default class Deque {
  /**
   * The array holding the deque items.
   * @type {number[][]}
   */
  items: number[][];

  /**
   * Creates an instance of Deque.
   */
  constructor() {
    this.items = [];
  }

  /**
   * Adds an element to the back of the deque.
   * @param elements - Elements to be inserted.
   */
  push(...elements: any[]): void {
    this.items.push(...elements);
  }

  /**
   * Adds an element to the back of the deque.
   * @param elements - Elements to be inserted.
   */
  add(...elements: any[]): void {
    this.items = [...elements];
  }

  /**
   * Removes and returns the element at the front of the deque.
   * @returns The removed element from the front.
   */
  shift(): number[] | undefined {
    return this.items.shift();
  }

  /**
   * Returns the number of elements in the deque.
   * @returns The size of the deque.
   */
  size(): number {
    return this.items.length;
  }

  /**
   * Returns the element at the front of the deque without removing it.
   * @returns The front element.
   */
  front(): number[] {
    return this.items[0];
  }

  /**
   * Returns the element at the back of the deque without removing it.
   * @returns The back element.
   */
  back(): number[] {
    return this.items[this.items.length - 1];
  }

  /**
   * Returns an array representation of the deque.
   * @returns The array of deque elements.
   */
  toArray(): number[][] {
    return this.items;
  }

  /**
   * Removed data out of duration.
   * @param timePeriod - Time period.
   */
  prune(timePeriod: number): void {
    const now = Date.now();
    while (this.items.length && now - this.items[0][0] > timePeriod) {
      this.shift();
    }
  }
}

/**
 * Manages the queue by adding new data and pruning old data.
 * @param  queue - The queue to be managed.
 * @param  newData - The new data point to be added.
 * @param  timePeriod - The time period in milliseconds.
 * @returns - The updated queue.
 */
export const manageQueue = (queue: Deque, newData: number[][], timePeriod: number): Deque => {
  newData.forEach((dataPoint) => {
    if (!queue.toArray().some((item) => item[0] === dataPoint[0])) {
      queue.push(dataPoint);
    }
  });

  queue.prune(timePeriod);
  return queue;
};
